import React, { useEffect, useRef, useState } from 'react';

// ============================================================
// Componente reutilizável para buscar itens na API GIX
// ============================================================

type Opcao = { codigo: number | string; descricao: string; [k: string]: any };

interface AutocompleteProps {
  endpoint: string;              // ex: "/api/gix/fornecedores"
  valor: number | string | null;  // código selecionado
  descricaoInicial?: string;      // descrição carregada (para exibir ao reabrir)
  onSelecionar: (op: Opcao | null) => void;
  placeholder?: string;
}

const GixAutocomplete: React.FC<AutocompleteProps> = ({ endpoint, valor, descricaoInicial, onSelecionar, placeholder }) => {
  const [query, setQuery] = useState<string>(descricaoInicial || (valor != null ? String(valor) : ''));
  const [aberto, setAberto] = useState(false);
  const [opcoes, setOpcoes] = useState<Opcao[]>([]);
  const [carregando, setCarregando] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<any>(null);

  useEffect(() => {
    setQuery(descricaoInicial || (valor != null ? String(valor) : ''));
  }, [descricaoInicial, valor]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const buscar = (q: string) => {
    setCarregando(true);
    fetch(`${endpoint}?q=${encodeURIComponent(q)}&limit=20`)
      .then(r => r.json())
      .then(j => {
        setOpcoes(j?.results || []);
        setCarregando(false);
      })
      .catch(() => setCarregando(false));
  };

  const onChange = (v: string) => {
    setQuery(v);
    setAberto(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => buscar(v), 250);
  };

  return (
    <div ref={boxRef} className="relative">
      <input
        type="text"
        value={query}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => { setAberto(true); if (opcoes.length === 0) buscar(query); }}
        placeholder={placeholder || 'Digite para buscar...'}
        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
      />
      {valor != null && (
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-500 bg-white px-1">#{valor}</span>
      )}
      {aberto && (
        <div className="absolute z-30 bg-white border border-gray-200 rounded shadow-lg mt-0.5 w-full max-h-60 overflow-auto">
          {carregando && <div className="px-2 py-1 text-xs text-gray-500">Buscando...</div>}
          {!carregando && opcoes.length === 0 && <div className="px-2 py-1 text-xs text-gray-500">Nenhum resultado</div>}
          {opcoes.map(op => (
            <div
              key={op.codigo}
              className="px-2 py-1 text-xs hover:bg-blue-50 cursor-pointer"
              onClick={() => { onSelecionar(op); setQuery(op.descricao); setAberto(false); }}
            >
              <span className="text-gray-500 mr-1">#{op.codigo}</span>
              <span>{op.descricao}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================
// Form principal de "Criar OC"
// ============================================================

export interface CriarOcConfig {
  empresa: number;
  fornecedor: number | null;
  fornecedor_desc?: string;
  condicao: number | null;
  condicao_desc?: string;
  centro_custo: number | null;
  centro_custo_desc?: string;
  tipo_despesa: number | null;
  tipo_despesa_desc?: string;
  tipo_movimento: string; // E18 (default uso/consumo), E01 (mercadoria), etc.
  data_prazo: string; // YYYY-MM-DD
  icms_simples: number;
  observacao: string;
  produto: number | null;
  produto_desc?: string;
  produto_unidade?: string;
  quantidade: number;
  preco_unitario: number;
}

const DEFAULT_CONFIG: CriarOcConfig = {
  empresa: 1,
  fornecedor: null,
  condicao: null,
  centro_custo: null,
  tipo_despesa: null,
  tipo_movimento: 'E18',
  data_prazo: new Date(Date.now() + 15 * 24 * 3600 * 1000).toISOString().slice(0, 10),
  icms_simples: 0,
  observacao: '',
  produto: null,
  quantidade: 1,
  preco_unitario: 0,
};

interface Props {
  config?: Partial<CriarOcConfig>;
  onChange?: (c: CriarOcConfig) => void;
  onSucesso?: (numeroOc: number, corccpri: number | undefined, raw: any) => void;
}

const GixCriarOcForm: React.FC<Props> = ({ config, onChange, onSucesso }) => {
  const [cfg, setCfg] = useState<CriarOcConfig>({ ...DEFAULT_CONFIG, ...(config || {}) });
  const [resultado, setResultado] = useState<any>(null);
  const [enviando, setEnviando] = useState(false);
  const [empresas, setEmpresas] = useState<Array<{codigo: number; descricao: string}>>([]);

  useEffect(() => {
    fetch('/api/gix/empresas')
      .then(r => r.json())
      .then(j => { if (j?.status === 'ok' && Array.isArray(j.results)) setEmpresas(j.results); })
      .catch(() => {});
  }, []);

  const up = (patch: Partial<CriarOcConfig>) => {
    const novo = { ...cfg, ...patch };
    setCfg(novo);
    onChange?.(novo);
  };

  const montarPayload = () => ({
    empresa: cfg.empresa,
    fornecedor: cfg.fornecedor,
    condicao_pagamento: cfg.condicao,
    centro_custo: cfg.centro_custo,
    tipo_despesa: cfg.tipo_despesa,
    tipo_movimento: cfg.tipo_movimento || 'E18',
    data_prazo: cfg.data_prazo,
    data_faturamento: cfg.data_prazo,
    icms_simples: Number(cfg.icms_simples) || 0,
    observacao: cfg.observacao,
    itens: [{
      produto: cfg.produto,
      quantidade: Number(cfg.quantidade),
      unidade: cfg.produto_unidade || 'PC',
      preco_unitario: Number(cfg.preco_unitario),
      prazo: cfg.data_prazo,
    }],
  });

  const testar = async () => {
    setEnviando(true);
    setResultado(null);
    try {
      const r = await fetch('/api/gix/oc', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(montarPayload()),
      });
      const j = await r.json();
      setResultado({ http: r.status, body: j });
      if (r.ok && j?.status === 'ok' && j?.dry_run === false && j?.numero_oc) {
        onSucesso?.(j.numero_oc, j.corccpri, j);
      }
    } catch (e: any) {
      setResultado({ erro: e?.message || String(e) });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="mt-2 p-3 bg-white border border-green-300 rounded space-y-2">
      <div className="text-xs font-semibold text-green-800 mb-1">Criar OC manualmente (1 item)</div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-[11px] text-gray-600 mb-0.5">Empresa</label>
          <select
            value={cfg.empresa}
            onChange={(e) => up({ empresa: Number(e.target.value) })}
            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
          >
            {empresas.length > 0 ? (
              empresas.map(emp => (
                <option key={emp.codigo} value={emp.codigo}>
                  {emp.codigo} — {emp.descricao}
                </option>
              ))
            ) : (
              <>
                <option value={1}>1 — Matriz</option>
                <option value={cfg.empresa}>{cfg.empresa}</option>
              </>
            )}
          </select>
        </div>
        <div>
          <label className="block text-[11px] text-gray-600 mb-0.5">Data prazo entrega</label>
          <input
            type="date"
            value={cfg.data_prazo}
            onChange={(e) => up({ data_prazo: e.target.value })}
            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
          />
        </div>
      </div>

      <div>
        <label className="block text-[11px] text-gray-600 mb-0.5">Fornecedor</label>
        <GixAutocomplete
          endpoint="/api/gix/fornecedores"
          valor={cfg.fornecedor}
          descricaoInicial={cfg.fornecedor_desc}
          onSelecionar={(op) => up({ fornecedor: op ? Number(op.codigo) : null, fornecedor_desc: op?.descricao || '' })}
          placeholder="Buscar fornecedor..."
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-[11px] text-gray-600 mb-0.5">Condição pagamento</label>
          <GixAutocomplete
            endpoint="/api/gix/condicoes"
            valor={cfg.condicao}
            descricaoInicial={cfg.condicao_desc}
            onSelecionar={(op) => up({ condicao: op ? Number(op.codigo) : null, condicao_desc: op?.descricao || '' })}
          />
        </div>
        <div>
          <label className="block text-[11px] text-gray-600 mb-0.5">Centro de custo</label>
          <GixAutocomplete
            endpoint="/api/gix/centros-custo"
            valor={cfg.centro_custo}
            descricaoInicial={cfg.centro_custo_desc}
            onSelecionar={(op) => up({ centro_custo: op ? Number(op.codigo) : null, centro_custo_desc: op?.descricao || '' })}
          />
        </div>
      </div>

      <div>
        <label className="block text-[11px] text-gray-600 mb-0.5">Tipo de despesa</label>
        <GixAutocomplete
          endpoint="/api/gix/tipos-despesa"
          valor={cfg.tipo_despesa}
          descricaoInicial={cfg.tipo_despesa_desc}
          onSelecionar={(op) => up({ tipo_despesa: op ? Number(op.codigo) : null, tipo_despesa_desc: op?.descricao || '' })}
        />
      </div>

      <div>
        <label className="block text-[11px] text-gray-600 mb-0.5">Tipo de movimento</label>
        <select
          value={cfg.tipo_movimento || 'E18'}
          onChange={(e) => up({ tipo_movimento: e.target.value })}
          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
        >
          <option value="E18">E18 — Entrada uso e consumo sem estoque (padrão)</option>
          <option value="E01">E01 — Compra de mercadoria</option>
          <option value="E02">E02 — Entrada especial</option>
          <option value="R04">R04 — Retorno</option>
          <option value="PRE">PRE — Pré-ordem</option>
        </select>
      </div>

      <div className="border-t pt-2 mt-2">
        <div className="text-xs font-semibold text-gray-700 mb-1">Item</div>
        <div>
          <label className="block text-[11px] text-gray-600 mb-0.5">Produto</label>
          <GixAutocomplete
            endpoint="/api/gix/produtos"
            valor={cfg.produto}
            descricaoInicial={cfg.produto_desc}
            onSelecionar={(op) => up({
              produto: op ? Number(op.codigo) : null,
              produto_desc: op?.descricao || '',
              produto_unidade: op?.unidade || 'PC',
            })}
          />
        </div>

        <div className="grid grid-cols-3 gap-2 mt-2">
          <div>
            <label className="block text-[11px] text-gray-600 mb-0.5">Qtd</label>
            <input
              type="number"
              min={0}
              step="0.01"
              value={cfg.quantidade}
              onChange={(e) => up({ quantidade: Number(e.target.value) })}
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
            />
          </div>
          <div>
            <label className="block text-[11px] text-gray-600 mb-0.5">Unidade</label>
            <input
              type="text"
              value={cfg.produto_unidade || ''}
              onChange={(e) => up({ produto_unidade: e.target.value })}
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
            />
          </div>
          <div>
            <label className="block text-[11px] text-gray-600 mb-0.5">Preço unit.</label>
            <input
              type="number"
              min={0}
              step="0.01"
              value={cfg.preco_unitario}
              onChange={(e) => up({ preco_unitario: Number(e.target.value) })}
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mt-2">
        <div>
          <label className="block text-[11px] text-gray-600 mb-0.5">ICMS Simples (%)</label>
          <input
            type="number"
            min={0}
            step="0.01"
            value={cfg.icms_simples}
            onChange={(e) => up({ icms_simples: Number(e.target.value) })}
            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
          />
        </div>
        <div>
          <label className="block text-[11px] text-gray-600 mb-0.5">Observação</label>
          <input
            type="text"
            value={cfg.observacao}
            onChange={(e) => up({ observacao: e.target.value })}
            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
          />
        </div>
      </div>

      <div className="flex items-center gap-2 pt-2 border-t mt-2">
        <button
          type="button"
          onClick={testar}
          disabled={enviando}
          className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"
        >
          {enviando ? 'Enviando...' : 'Criar OC manual no GIX'}
        </button>
        <span className="text-[10px] text-gray-500">
          Cria a OC de verdade no GIX com os dados acima (se a API devolver "dry_run", nada foi gravado).
        </span>
      </div>

      {resultado && (
        <pre className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded text-[10px] overflow-auto max-h-60">
          {JSON.stringify(resultado, null, 2)}
        </pre>
      )}
    </div>
  );
};

export default GixCriarOcForm;
